"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/nextjs";
import { useEffect } from "react";
import { toast } from "sonner";
import { NOTIFICATION_QUERY_KEYS, notificationSummaryQueryOptions } from "@/lib/notification-summary-query";
import { isServerActionNotFoundError } from "@/lib/serverActionUtils";
import {
  getNotifications,
  markNotificationsRead,
  type NotificationListResult,
  type NotificationSummaryResult,
} from "@/lib/actions/notifications";

export { NOTIFICATION_QUERY_KEYS } from "@/lib/notification-summary-query";

/** Clerk `useUser()` 는 판별 유니온이라 `isSignedIn` 이 true 면 로드 완료·`user` 존재가 보장된다. */
function useNotificationIdentity() {
  const { isSignedIn, user } = useUser();
  return { enabled: isSignedIn === true, userId: user?.id ?? null };
}

/**
 * 벨 뱃지용 미읽음 개수 (JCG-3601). 로그인 상태에서만 조회하고 1분마다·창 포커스 시 갱신한다.
 * `useExamDday` 와 같은 React Query 패턴 — 서버 액션을 queryFn 으로 직접 쓴다.
 */
export function useUnreadNotificationCount() {
  const { enabled, userId } = useNotificationIdentity();
  const query = useQuery(notificationSummaryQueryOptions(userId, enabled));
  const needsReload = isServerActionNotFoundError(query.error);
  useEffect(() => {
    if (!needsReload) return;
    toast.error("새 버전이 배포되어 알림 조회를 멈췄습니다. 페이지를 새로고침해 주세요.", {
      id: "notification-version-mismatch",
      duration: Infinity,
      action: { label: "새로고침", onClick: () => window.location.reload() },
    });
  }, [needsReload]);
  return {
    enabled,
    unreadCount: query.data?.success ? query.data.unreadCount : 0,
  };
}

/**
 * 팝오버가 열렸을 때만 목록을 읽는다. `enabled` 가 false→true 로 바뀔 때 `staleTime: 0` 이라 매번 최신으로 읽는다.
 * 목록 응답의 `unreadCount` 로 뱃지 쿼리 캐시를 덮어써 팝오버 헤더·"모두 읽음"·뱃지가 같은 시점의 값을 쓰게 한다
 * (뱃지 쿼리는 30초 stale 이라 그 사이 온 답글이 목록에만 보이는 어긋남을 막는다).
 * 덮어쓰기는 **실제 fetch 안(queryFn)** 에서만 한다 — effect 로 하면 새 벨 인스턴스(드로어)가 마운트될 때 캐시된
 * 옛 목록 값으로 읽음 처리 뒤의 최신 뱃지를 되돌린다.
 */
export function useNotificationList(open: boolean) {
  const { enabled: signedIn, userId } = useNotificationIdentity();
  const enabled = open && signedIn;
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: NOTIFICATION_QUERY_KEYS.list(userId),
    queryFn: async () => {
      const result = await getNotifications();
      if (result.success) {
        const summary: NotificationSummaryResult = {
          success: true,
          unreadCount: result.unreadCount,
        };
        queryClient.setQueryData(NOTIFICATION_QUERY_KEYS.unread(userId), summary);
      }
      return result;
    },
    enabled,
    staleTime: 0,
  });

  const data = query.data;
  return {
    items: data?.success ? data.items : [],
    isPending: enabled && query.isPending,
    isError: data ? !data.success : query.isError,
    refetch: query.refetch,
  };
}

/**
 * 읽음 처리. 성공하면 서버를 다시 부르지 않고 응답(`updated`)과 넘긴 `ids` 로 목록·뱃지 캐시를 직접 고친다
 * — 목록 fetch 는 이미 뱃지를 함께 갱신하므로 무효화하면 서버 액션이 두 번 더 돈다. 실패했을 때만 무효화해 재동기화한다.
 */
export function useMarkNotificationsRead() {
  const queryClient = useQueryClient();
  const { userId } = useNotificationIdentity();
  return useMutation({
    mutationFn: (ids?: string[]) => markNotificationsRead(ids ? { ids } : {}),
    onSuccess: (result, ids) => {
      if (!result.success) {
        void queryClient.invalidateQueries({ queryKey: NOTIFICATION_QUERY_KEYS.all });
        return;
      }
      const readAt = new Date().toISOString();
      const targets = ids ? new Set(ids) : null;
      queryClient.setQueryData<NotificationListResult>(
        NOTIFICATION_QUERY_KEYS.list(userId),
        (prev) => {
          if (!prev?.success) return prev;
          const items = prev.items.map((item) =>
            item.readAt === null && (!targets || targets.has(item.id))
              ? { ...item, readAt }
              : item,
          );
          return { ...prev, items, unreadCount: items.filter((item) => item.readAt === null).length };
        },
      );
      queryClient.setQueryData<NotificationSummaryResult>(
        NOTIFICATION_QUERY_KEYS.unread(userId),
        (prev) => {
          const before = prev?.success ? prev.unreadCount : 0;
          return { success: true, unreadCount: targets ? Math.max(0, before - result.updated) : 0 };
        },
      );
    },
  });
}
